import { ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { useOrderCreationStore, ORDER_CREATION_STEPS } from '@/store/orderCreationStore'
import { useLocalization } from '@/contexts/LocalizationContext'
import { ArrowLeft, X, Check } from 'lucide-react'
import { cn } from '@/lib/utils'

interface OrderCreationLayoutProps {
  children: ReactNode
  /** Translation key for title (e.g. orderCreation.selectClient) so it works in both en/ar */
  titleKey?: string
  /** Optional translation key for description */
  descriptionKey?: string
  /** Title string (used when titleKey not provided) */
  title?: string
  titleAr?: string
  description?: string
  descriptionAr?: string
  step: number
  showBack?: boolean
  backPath?: string
  onBack?: () => void
  showCancel?: boolean
}

export function OrderCreationLayout({
  children,
  titleKey,
  descriptionKey,
  title: titleLegacy,
  titleAr,
  description: descLegacy,
  descriptionAr,
  step,
  showBack = true,
  backPath,
  onBack,
  showCancel = true,
}: OrderCreationLayoutProps) {
  const navigate = useNavigate()
  const { locale, t } = useLocalization()
  const { reset, getProgress, setCurrentStep } = useOrderCreationStore()
  const displayTitle = titleKey ? t(titleKey) : (locale === 'ar' && titleAr ? titleAr : titleLegacy ?? '')
  const displayDescription = descriptionKey ? t(descriptionKey) : (locale === 'ar' && descriptionAr ? descriptionAr : descLegacy)

  const handleCancel = () => {
    if (confirm(t('orderCreation.cancelConfirm'))) {
      reset()
      navigate('/orders')
    }
  }

  const handleBack = () => {
    if (onBack) {
      onBack()
    } else if (backPath) {
      navigate(backPath)
    } else {
      navigate(-1)
    }
  }

  const progress = getProgress()

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background border-b shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {showBack && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleBack}
                  className="gap-2"
                >
                  <ArrowLeft className="h-4 w-4" />
                  <span className="hidden sm:inline">
                    {t('orderCreation.back')}
                  </span>
                </Button>
              )}
              <div>
                <h1 className="text-xl font-semibold text-foreground">{displayTitle}</h1>
                {displayDescription && (
                  <p className="text-sm text-muted-foreground hidden sm:block">
                    {displayDescription}
                  </p>
                )}
              </div>
            </div>
            {showCancel && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleCancel}
                className="text-muted-foreground hover:text-destructive"
              >
                <X className="h-4 w-4" />
                <span className="hidden sm:inline ms-2">
                  {t('orderCreation.cancel')}
                </span>
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* Progress Stepper */}
      <div className="bg-muted/30 border-b">
        <div className="container mx-auto px-4 py-4">
          {/* Step indicators - Desktop */}
          <div className="hidden md:flex items-center justify-between mb-3">
            {ORDER_CREATION_STEPS.map((s, index) => (
              <div key={s.number} className="flex items-center">
                <div className="flex flex-col items-center">
                  <div
                    className={cn(
                      'w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium transition-all duration-200',
                      s.number < step
                        ? 'bg-primary text-primary-foreground'
                        : s.number === step
                          ? 'bg-primary text-primary-foreground ring-4 ring-primary/20'
                          : 'bg-muted text-muted-foreground'
                    )}
                  >
                    {s.number < step ? (
                      <Check className="h-5 w-5" />
                    ) : (
                      s.number
                    )}
                  </div>
                  <span
                    className={cn(
                      'mt-2 text-xs font-medium',
                      s.number <= step ? 'text-foreground' : 'text-muted-foreground'
                    )}
                  >
                    {t(`orderCreation.step${s.number === 1 ? 'Client' : s.number === 2 ? 'Category' : s.number === 3 ? 'Template' : s.number === 4 ? 'Schedule' : s.number === 5 ? 'Beneficiary' : s.number === 6 ? 'Questions' : 'Payment'}`)}
                  </span>
                </div>
                {index < ORDER_CREATION_STEPS.length - 1 && (
                  <div
                    className={cn(
                      'flex-1 h-0.5 mx-2 transition-colors duration-200',
                      s.number < step ? 'bg-primary' : 'bg-muted'
                    )}
                  />
                )}
              </div>
            ))}
          </div>

          {/* Step indicators - Mobile (simplified) */}
          <div className="md:hidden flex items-center justify-between mb-3">
            {ORDER_CREATION_STEPS.map((s) => (
              <div
                key={s.number}
                className={cn(
                  'w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium transition-all',
                  s.number < step
                    ? 'bg-primary text-primary-foreground'
                    : s.number === step
                      ? 'bg-primary text-primary-foreground ring-2 ring-primary/20'
                      : 'bg-muted text-muted-foreground'
                )}
              >
                {s.number < step ? <Check className="h-3 w-3" /> : s.number}
              </div>
            ))}
          </div>

          {/* Progress bar */}
          <div className="w-full bg-muted rounded-full h-2 overflow-hidden">
            <div
              className="bg-primary h-2 rounded-full transition-all duration-500 ease-out"
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="flex justify-between mt-2 text-xs text-muted-foreground">
            <span>
              {t('orderCreation.stepOf', { step, total: 7 })}
            </span>
            <span>{progress}%</span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-6">
        {children}
      </main>
    </div>
  )
}
